<template>
  <section class='suppression-panel' v-if='current || history.length' :aria-label='$t("suppression.panel.title")'>
    <div class='suppression-panel__current' v-if='current'>
      <div class='suppression-panel__header'>
        <div class='suppression-panel__title'>
          {{ currentTypeLabel }}
          <suppression-badge v-if='isTemporaryCurrent' :suppression='current' />
        </div>
        <div class='suppression-panel__actions'>
          <!-- Restore lives in the dialog header, where Suppress was. -->
          <v-btn small depressed class='btn-secondary' @click='$emit("edit", current)'>
            {{ isTemporaryCurrent ? $t('suppression.action.edit.extend') : $t('suppression.action.edit') }}
          </v-btn>
        </div>
      </div>

      <p class='suppression-panel__legacy' v-if='isBackfilled'>
        {{ $t('suppression.panel.backfilled') }}
      </p>
      <dl class='suppression-panel__details' v-else>
        <div>
          <dt>{{ $t('suppression.field.by') }}</dt>
          <dd>{{ current.suppressedByName || $t('suppression.panel.unknown.user') }}</dd>
        </div>
        <div>
          <dt>{{ isTemporaryCurrent ? $t('suppression.field.start') : $t('suppression.field.date') }}</dt>
          <dd>{{ formatDate(current.suppressedAt) }}</dd>
        </div>
        <div>
          <dt>{{ $t('suppression.field.expires') }}</dt>
          <dd>{{ expiryText(current) }}</dd>
        </div>
        <div>
          <dt>{{ $t('suppression.field.reason') }}</dt>
          <dd>{{ reasonText(current) }}</dd>
        </div>
        <div class='suppression-panel__justification'>
          <dt>{{ $t('suppression.field.justification') }}</dt>
          <dd>{{ current.justification }}</dd>
        </div>
      </dl>
    </div>

    <div class='suppression-panel__history' v-if='history.length'>
      <button
        type='button'
        class='suppression-panel__history-toggle'
        :aria-expanded='String(historyOpen)'
        @click='historyOpen = !historyOpen'
      >
        <span class='material-icons' aria-hidden='true'>{{ historyOpen ? 'expand_less' : 'expand_more' }}</span>
        {{ $t('suppression.panel.history', { count: history.length }) }}
      </button>
      <ol class='suppression-panel__timeline' v-if='historyOpen'>
        <li v-for='entry in history' :key='entry.id'>
          <div class='suppression-panel__event'>{{ suppressedEventText(entry) }}</div>
          <div class='suppression-panel__event-detail'>{{ reasonText(entry) }} · {{ entry.justification }}</div>
          <div class='suppression-panel__event' v-if='entry.restoredAt'>{{ endedEventText(entry) }}</div>
        </li>
      </ol>
    </div>
  </section>
</template>

<script>
import SuppressionBadge from '@/components/suppression/SuppressionBadge.vue';
import {
  SUPPRESSION_ORIGINS, SUPPRESSION_REASONS, SUPPRESSION_RESTORE_SOURCES, SUPPRESSION_TYPES,
} from '@/constants/suppression';
import { formatSuppressionDate } from '@/utils/suppression';

export default {
  name: 'SuppressionPanel',
  components: { SuppressionBadge },
  props: {
    /** Suppression history of the finding, newest first (from GET /issues/:id). */
    history: {
      type: Array,
      default: () => [],
    },
    /** The issue's own effective suppression (from the list), used until or if history doesn't load. */
    fallback: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      historyOpen: false,
    };
  },
  computed: {
    current() {
      return this.history.find(entry => entry.isEffective) || this.fallback || null;
    },
    isTemporaryCurrent() {
      return this.current && this.current.type === SUPPRESSION_TYPES.TEMPORARY;
    },
    isBackfilled() {
      return this.current && this.current.origin === SUPPRESSION_ORIGINS.BACKFILL;
    },
    currentTypeLabel() {
      return this.isTemporaryCurrent ? this.$t('suppression.type.temporary') : this.$t('suppression.badge.suppressed');
    },
  },
  methods: {
    formatDate(date) {
      return formatSuppressionDate(date);
    },
    reasonText(entry) {
      const reason = this.$t(`suppression.reason.${entry.reason}`);
      return entry.reason === SUPPRESSION_REASONS.OTHER && entry.reasonOther ? `${reason}: ${entry.reasonOther}` : reason;
    },
    expiryText(entry) {
      if (entry.type !== SUPPRESSION_TYPES.TEMPORARY) {
        return this.$t('suppression.expiry.never');
      }
      return this.formatDate(entry.expiresAt);
    },
    suppressedEventText(entry) {
      const key = entry.type === SUPPRESSION_TYPES.TEMPORARY ? 'suppression.history.temporary' : 'suppression.history.permanent';
      return this.$t(key, {
        date: this.formatDate(entry.suppressedAt),
        name: entry.suppressedByName || this.$t('suppression.panel.unknown.user'),
        expires: this.formatDate(entry.expiresAt),
      });
    },
    endedEventText(entry) {
      const params = {
        date: this.formatDate(entry.restoredAt),
        name: entry.restoredByName || this.$t('suppression.panel.unknown.user'),
      };
      if (entry.restoreSource === SUPPRESSION_RESTORE_SOURCES.EXPIRED) {
        return this.$t('suppression.history.expired', params);
      }
      if (entry.restoreSource === SUPPRESSION_RESTORE_SOURCES.REPLACED) {
        return this.$t('suppression.history.replaced', params);
      }
      return this.$t('suppression.history.restored', params);
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.suppression-panel {
  margin: 0 $space-8 $space-6 0;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: $space-4;
  }

  &__title {
    color: var(--text-primary);
    font-family: $font-family-semibold;
    font-size: $font-size-base;
  }

  &__actions {
    display: flex;
    gap: $space-2;

    .v-btn {
      height: $btn-height-md;
      border-radius: $radius-sm;
    }
  }

  &__legacy {
    margin: $space-3 0 0;
    color: var(--text-secondary);
    font-size: $font-size-sm;
  }

  &__details {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: $space-3 $space-4;
    margin: $space-4 0 0;

    dt {
      color: var(--text-secondary);
      font-size: $font-size-xs;
      letter-spacing: 0.8px;
      text-transform: uppercase;
    }

    dd {
      margin: 0;
      color: var(--text-primary);
      font-size: $font-size-sm;
    }
  }

  &__justification {
    grid-column: 1 / -1;

    dd {
      white-space: pre-line;
      user-select: text;
    }
  }

  &__history {
    margin-top: $space-4;
  }

  &__history-toggle {
    display: inline-flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    color: var(--brand-primary);
    font-size: $font-size-sm;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--brand-primary);
      outline-offset: 2px;
    }

    .material-icons {
      font-size: $font-size-md;
    }
  }

  &__timeline {
    list-style: none;
    margin: $space-2 0 0;
    padding: 0;

    li {
      padding: $space-2 0 $space-2 $space-3;
      border-left: 2px solid var(--border-primary);
    }
  }

  &__event {
    color: var(--text-primary);
    font-size: $font-size-sm;
  }

  &__event-detail {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    overflow-wrap: anywhere;
  }
}
</style>
